<template>
  <div class="position-relative">
    <label v-if="label" :for="id" class="form-label">
      {{ label }}
      <span v-if="validate.includes('required') && !disabled && !showAsInfo" class="required-status">*</span>
    </label>

    <Field
      v-if="!showAsInfo"
      v-slot="{ field, meta }"
      :name="name || id"
      :modelValue="modelValue"
      @input="$emit('update:modelValue', $event || '')"
      :rules="!disabled ? validate : ''"
    >
      <div v-if="loading" class="placeholder-glow">
        <span class="placeholder col-8"></span>
      </div>
      <Multiselect
        v-else
        :class="{ 'is-invalid': (Array.isArray(errors) && errors.length) || meta?.errors?.length }"
        :id="id"
        v-bind="field"
        :options="options"
        :searchable="searchable"
        :placeholder="placeholder"
        :disabled="disabled"
        :mode="mode == 'tags' || mode == 'multiple' ? mode : 'single'"
        :multiple="mode == 'tags' || mode == 'multiple' ? true : false"
        :close-on-select="mode == 'tags' || mode == 'multiple' ? false : true"
        :object="object"
        :noOptionsText="$t('main.thereAreNoData')"
        :noResultsText="$t('main.thereAreNoResults')"
        :multiple-label="(values) => `${values.length} ${$t('main.selected')}`"
      />

      <ul v-if="sortable" class="draggable-list mt-3" ref="listRef" @dragover.prevent="onDragOver">
        <li
          v-for="(item, index) in modelValue"
          :key="index"
          draggable="true"
          @dragstart="onDragStart(index)"
          @dragend="onDragEnd"
          :class="{ dragging: draggedIndex === index }"
        >
          <span class="draggable-list-item">
            <span>{{ options.find((i) => i.value == item)?.label }}</span>
            <span
              @click="
                $emit(
                  'update:modelValue',
                  modelValue.filter((_, i) => i !== index),
                )
              "
            >
              <i class="mdi mdi-delete text-danger"></i>
            </span>
          </span>
        </li>
      </ul>

      <div
        v-if="(Array.isArray(errors) && errors.length) || errors.length || meta?.errors?.length"
        class="invalid-feedback"
      >
        {{ Array.isArray(errors) ? errors[0] : errors }}
        {{ Array.isArray(meta.errors) ? meta.errors[0] : '' }}
      </div>
    </Field>
    <span v-else class="main-select-text form-control">{{ text }}</span>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref } from 'vue';
import Multiselect from '@vueform/multiselect';
import '@vueform/multiselect/themes/default.css';

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
  modelValue: { type: [String, Array], default: () => [] },
  id: { type: String, default: '' },
  name: { type: String, default: '' }, // for validation
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  options: { type: Array, default: () => [] },
  mode: { type: String, default: '' },
  object: { type: Boolean, default: false },
  searchable: { type: Boolean, default: true },
  sortable: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  showAsInfo: { type: Boolean, default: false },
  text: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  validate: { type: [String, Object], default: '' },
  errors: { type: [String, Array], default: '' },
});

const draggedIndex = ref(null);
const listRef = ref(null);

const onDragStart = (index) => {
  draggedIndex.value = index;
};

const onDragEnd = () => {
  draggedIndex.value = null;
};

const onDragOver = (event) => {
  if (!listRef.value || !listRef.value.children) return;

  if (draggedIndex.value === null) return;

  const draggedItem = JSON.parse(JSON.stringify(props.modelValue[draggedIndex.value]));
  const divs = Array.from(listRef.value.children).filter((el) => !el.classList.contains('dragging'));

  for (let i = 0; i < divs.length; i++) {
    const rect = divs[i].getBoundingClientRect();
    const offset = event.clientY - rect.top - rect.height / 2;
    if (offset < 0) {
      const newIndex = i;
      if (newIndex !== draggedIndex.value) {
        const newList = [...props.modelValue];
        newList.splice(draggedIndex.value, 1);
        newList.splice(newIndex, 0, draggedItem);
        emit('update:modelValue', newList);
        draggedIndex.value = newIndex;
      }
      break;
    }
  }
};
</script>

<style lang="scss" scoped>
.placeholder-glow {
  border: 1px solid;
  padding: 0.5rem 0.9rem;
  border: var(--vz-border-width) solid var(--vz-input-border-custom);
  border-radius: var(--vz-border-radius);
}
.main-select-text {
  background-color: var(--vz-tertiary-bg);
  min-height: 40px;
}
.draggable-list-item {
  width: calc(100% - 16px);
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}
</style>
